import { Check, Stroke } from '@sds-system/assets';
import { useTheme } from '@sds-system/system';
import type { PolymorphicRef } from '@sds-system/types';
import { processNestedCSS, useForkRef } from '@sds-system/utils';
import type { ChangeEventHandler, ElementType } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Box } from '../Box';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import { StyledCheckBoxRoot, StyledCheckInput } from './Checkbox.styled';
import type { CheckboxComponent, CheckboxProps } from './Checkbox.types';

export const Checkbox: CheckboxComponent = forwardRef(
  <C extends ElementType = 'input'>(
    {
      disabled,
      value,
      name,
      label,
      checkboxSize = 'XS',
      color = 'system.white',
      isIndeterminate = false,
      styledComponents,
      onChange,
      checked,
      icon,
      ...props
    }: CheckboxProps<C>,
    ref: PolymorphicRef<C>,
  ) => {
    const localCheckboxRef = useRef<HTMLInputElement | null>(null);
    const checkboxRef = useForkRef(ref, localCheckboxRef);
    const [localChecked, setLocalChecked] = useState(false);
    const theme = useTheme();

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      setLocalChecked(e.target.checked);
      if (onChange) onChange(e);
    };

    useEffect(() => {
      setLocalChecked(checked || localCheckboxRef?.current?.checked || false);
    }, []);

    return (
      <StyledCheckBoxRoot
        className={`${value}-checkbox-root checkbox-root`}
        htmlFor={`${name || ''}-${value || ''}`}
        {...styledComponents?.root}
      >
        <Box
          css={{
            svg: processNestedCSS(
              {
                color,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%,-50%)`,
              },
              theme,
            ),
          }}
          height={theme.size[checkboxSize]}
          minHeight={theme.size[checkboxSize]}
          minWidth={theme.size[checkboxSize]}
          position='relative'
          width={theme.size[checkboxSize]}
        >
          <StyledCheckInput
            checkboxSize={checkboxSize}
            checked={checked}
            disabled={disabled}
            id={`${name || ''}-${value || ''}`}
            name={name}
            onChange={handleChange}
            value={value}
            {...props}
            {...styledComponents?.input}
            ref={checkboxRef}
            type='checkbox'
          />
          {checked || localChecked
            ? icon ||
              (isIndeterminate ? (
                <Icon height={2} minHeight={2} minWidth={12} source={Stroke} width={12} />
              ) : (
                <Icon height={9} minHeight={9} minWidth={12} source={Check} width={12} />
              ))
            : null}
        </Box>
        {typeof label === 'string' ? (
          <Typography fontType='body2.regular' {...styledComponents?.label}>
            {label}
          </Typography>
        ) : (
          label
        )}
      </StyledCheckBoxRoot>
    );
  },
);
